import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import {
  RecurringInvoice,
  RecurringInvoiceCancellationReason,
  RecurringInvoiceTip,
  SettingsRecurringInvoice,
} from '../models/invoice.model';

@Injectable({
  providedIn: 'root',
})
export class RecurringInvoiceService {
  constructor(private httpClient: HttpClient) {}
  saveRecurringInvoice<T>(payload: RecurringInvoice): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.recurringInvoiceAPIRoot}/saverecurringinvoice`,
      payload
    );
  }

  getRecurringInvoiceConfigurationByInvoiceNumber<T>(
    invoice: string
  ): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.recurringInvoiceAPIRoot}/getconfigurationbyinvoicenumber/${invoice}`
    );
  }

  getRecurringInvoiceConfigurationByChildInvoiceNumber<T>(
    invoice: string
  ): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.recurringInvoiceAPIRoot}/getconfigurationbychildinvoicenumber/${invoice}`
    );
  }

  getAllRecurringInvoice<T>(locationId: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.recurringInvoiceAPIRoot}/getallrecurringinvoices?locationId=${locationId}`
    );
  }

  getRecurringInvoiceById<T>(id: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.recurringInvoiceAPIRoot}/getrecurringinvoiceid/${id}`
    );
  }

  getRecurringInvoicesByRecurringId<T>(id: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.recurringInvoiceAPIRoot}/getinvoicesbyrecurringinvoiceid/${id}`
    );
  }

  deleteRecurringInvoicesByRecurringId<T>(
    id: number,
    updatedBy: number
  ): Observable<T> {
    return this.httpClient.delete<T>(
      `${environment.recurringInvoiceAPIRoot}/detail/${id}/delete/${updatedBy}`
    );
  }

  archive<T>(payload: any): Observable<T> {
    return this.httpClient.put<T>(
      `${environment.recurringInvoiceAPIRoot}/archive`,
      payload
    );
  }

  suspend<T>(payload: any): Observable<T> {
    return this.httpClient.put<T>(
      `${environment.recurringInvoiceAPIRoot}/suspend`,
      payload
    );
  }

  activate<T>(payload: any): Observable<T> {
    return this.httpClient.put<T>(
      `${environment.recurringInvoiceAPIRoot}/activate`,
      payload
    );
  }

  pause<T>(payload: any): Observable<T> {
    return this.httpClient.put<T>(
      `${environment.recurringInvoiceAPIRoot}/pause`,
      payload
    );
  }

  resume<T>(payload: any): Observable<T> {
    return this.httpClient.put<T>(
      `${environment.recurringInvoiceAPIRoot}/resume`,
      payload
    );
  }

  setRecurringInvoiceTipConfiguration<T>(
    payload: RecurringInvoiceTip
  ): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.recurringInvoiceAPIRoot}/savetipdetails`,
      payload
    );
  }

  getSuspensionsByRecurringId<T>(id: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.recurringInvoiceAPIRoot}/getsuspensionsbyrecurringinvoiceid/${id}`
    );
  }

  getRecurringInvoiceCancellations<T>(): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.recurringInvoiceAPIRoot}/getrecurringinvoicecancellations`
    );
  }

  approveCancellationRequest<T>(payload: any): Observable<T> {
    return this.httpClient.put<T>(
      `${environment.recurringInvoiceAPIRoot}/approvecancellationrequest`,
      payload
    );
  }

  getAllCancellationsForApproval<T>(): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.recurringInvoiceAPIRoot}/getcancellationsforapproval`
    );
  }

  getCancellationReasons<T>(): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.recurringInvoiceAPIRoot}/getcancellationreasons`
    );
  }

  upsertRecurringInvoiceCancellationReason<T>(
    payload: RecurringInvoiceCancellationReason
  ): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.recurringInvoiceAPIRoot}/upsertrecurringinvoicecancellationreason`,
      payload
    );
  }

  deleteCancellationReason<T>(id: number, deletedBy: number): Observable<T> {
    return this.httpClient.delete<T>(
      `${environment.recurringInvoiceAPIRoot}/cancellationreason/delete/${id}/${deletedBy}`
    );
  }

  getUnpaidManualInvoices<T>(recurringInvoicePaymentId: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.recurringInvoiceAPIRoot}/getunpaidmanualinvoices/${recurringInvoicePaymentId}`
    );
  }

  getSettings<T>(): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.recurringInvoiceAPIRoot}/getsettings`
    );
  }

  updateSettingsRecurringInvoice<T>(
    payload: SettingsRecurringInvoice
  ): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.recurringInvoiceAPIRoot}/updatesettings`,
      payload
    );
  }

  getRecurringPaymentFormDetails<T>(id: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.recurringInvoiceAPIRoot}/${id}/details`
    );
  }

  getRecurringPaymentFormChildInvoices<T>(id: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.recurringInvoiceAPIRoot}/getchildinvoices/${id}`
    );
  }

  getRecurringPaymentConfigByInvoiceNumber<T>(
    invoiceNumber: string
  ): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.recurringInvoiceAPIRoot}/getconfigurationbyinvoicenumber/${invoiceNumber}`
    );
  }

  getRecurringPaymentConfigById<T>(id: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.recurringInvoiceAPIRoot}/getconfigurationbyrecurringinvoiceid/${id}`
    );
  }
}
